/**
 * Validation utility for form fields
 */
import React from 'react';

// Validation rules for different field types
export const validationRules = {
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Please enter a valid email address'
  },
  password: {
    required: true,
    minLength: 8,
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/,
    message: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character !@#$%^&*(),.?":{}|<>'
  },
  confirmPassword: {
    required: true,
    match: 'password',
    message: 'Passwords do not match'
  },
  first_name: {
    required: true,
    minLength: 2,
    pattern: /^[a-zA-Z\s]+$/,
    message: 'First name must be at least 2 characters and contain only letters'
  },
  last_name: {
    required: true,
    minLength: 2,
    pattern: /^[a-zA-Z\s]+$/,
    message: 'Last name must be at least 2 characters and contain only letters'
  },
  tenant_code: {
    required: true,
    minLength: 2,
    message: 'Workspace code is required'
  }
};

/**
 * Validate a single field
 * @param {string} name - Field name
 * @param {string} value - Field value
 * @param {object} allFormData - All form data (for match validation)
 * @returns {string} Error message or empty string if valid
 */
export const validateField = (name, value, allFormData = {}) => {
  const rules = validationRules[name];
  if (!rules) return '';

  if (rules.required && (!value || value.trim() === '')) {
    return `${name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} is required`;
  }

  if (rules.minLength && value.length < rules.minLength) {
    return `Must be at least ${rules.minLength} characters long`;
  }

  if (rules.pattern && !rules.pattern.test(value)) {
    return rules.message;
  }

  if (rules.match && value !== allFormData[rules.match]) {
    return rules.message;
  }

  return '';
};

/**
 * Validate entire form based on specified fields
 * @param {object} formData - Form data object
 * @param {array} fields - Array of field names to validate
 * @returns {object} Object with errors and valid flags for each field
 */
export const validateForm = (formData, fields) => {
  const errors = {};
  const valid = {};
  
  fields.forEach(field => {
    const error = validateField(field, formData[field], formData);
    errors[field] = error;
    valid[field] = !error;
  });

  return { errors, valid, isValid: Object.values(errors).every(error => !error) };
};

/**
 * Hook for managing form validation state
 * @param {array} fields - Array of field names to validate
 * @returns {object} Validation state and handlers
 */
export const useFormValidation = (fields) => {
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldValid, setFieldValid] = React.useState({});
  const [touched, setTouched] = React.useState({});

  const handleBlur = (fieldName, value, formData) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    
    const error = validateField(fieldName, value, formData);
    setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
    setFieldValid(prev => ({ ...prev, [fieldName]: !error }));
  };

  const handleFieldChange = (fieldName, value, formData) => {
    if (touched[fieldName]) {
      const error = validateField(fieldName, value, formData);
      setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
      setFieldValid(prev => ({ ...prev, [fieldName]: !error }));
    }
  };

  const validateAllFields = (formData) => {
    const result = validateForm(formData, fields);
    setFieldErrors(result.errors);
    setFieldValid(result.valid);
    setTouched(fields.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    return result.isValid;
  };

  const getFieldClass = (fieldName) => {
    if (!touched[fieldName]) return '';
    return '';
  };

  return {
    fieldErrors,
    fieldValid,
    touched,
    handleBlur,
    handleFieldChange,
    validateAllFields,
    getFieldClass,
    setFieldErrors,
    setFieldValid,
    setTouched
  };
};
